import { AppBar, Toolbar, Box, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { UpliftBetterHelpIcon, UpliftBetterHelpStackedIcon } from '@uplift/icons';
import React, { useMemo } from 'react';
import { useContextualNavigationStyles } from './styles';
import { useIsMobile } from '@uplift/hooks';

export type ContextualNavigationHeaderProps = {
  leftButtonText?: string;
  onLeftButtonClick?: () => void;
  leftButton?: React.ReactNode;
  rightButtonText?: string;
  onRightButtonClick?: () => void;
  rightButton?: React.ReactNode;
  hideLogo?: boolean;
  centerBlock?: React.ReactNode;
  fullWidth?: boolean;
};

export const ContextualNavigationHeader: React.VFC<ContextualNavigationHeaderProps> = ({
  leftButtonText,
  onLeftButtonClick,
  leftButton,
  rightButtonText,
  onRightButtonClick,
  rightButton,
  hideLogo,
  centerBlock,
  fullWidth
}) => {
  const isMobile = useIsMobile();
  const { classes } = useContextualNavigationStyles({
    fullWidth: fullWidth ?? false
  });

  const left = useMemo(() => {
    if (leftButton) {
      return leftButton;
    }

    if (leftButtonText) {
      return (
        <Button
          type="button"
          color="primary"
          startIcon={<ArrowBack />}
          disabled={!onLeftButtonClick}
          onClick={onLeftButtonClick}
          className={classes.headerButton}
        >
          {leftButtonText}
        </Button>
      );
    }

    return null;
  }, [ classes.headerButton, leftButton, leftButtonText, onLeftButtonClick ]);

  const right = useMemo(() => {
    if (rightButton) {
      return rightButton;
    }

    if (rightButtonText && onRightButtonClick) {
      return (
        <Button
          type="button"
          color="primary"
          onClick={onRightButtonClick}
          className={classes.headerButton}
        >
          {rightButtonText}
        </Button>
      );
    }

    return null;
  }, [ classes.headerButton, onRightButtonClick, rightButton, rightButtonText ]);

  const center = useMemo(() => {
    if (centerBlock) {
      return (
        <Box flex={1}>
          {centerBlock}
        </Box>
      );
    }

    if (hideLogo) {
      return null;
    }

    if (isMobile) {
      return (
        <Box flex={1} className={classes.logo}>
          <UpliftBetterHelpStackedIcon />
        </Box>
      );
    }

    return (
      <Box flex={1} className={classes.logo}>
        <UpliftBetterHelpIcon sx={{ height: 30 }} />
      </Box>
    );
  }, [ centerBlock, classes.logo, hideLogo, isMobile ]);

  return (
    <AppBar position="static" elevation={0} className={classes.headerWrapper}>
      <Toolbar className={classes.header}>
        <Box flex={1}>
          {left}
        </Box>
        {center}
        <Box flex={1} display="flex" justifyContent="flex-end">
          {right}
        </Box>
      </Toolbar>
    </AppBar>
  );
};
